import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { SxProps, Theme } from '@mui/material';
import Chip, { ChipOwnProps } from '@mui/material/Chip';
import { OrderState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Order } from '~hooks/useOrders/models';
import { snakeCaseToHuman } from '~utils/utilFunctions';

export const PROPS_BY_ORDER_STATE: Partial<
  Record<OrderState, Pick<ChipOwnProps, 'color' | 'label'>>
> = {
  [OrderState.ACCEPTED]: { color: 'default', label: t('status.dispatching') },
  [OrderState.CANCELED]: { color: 'error', label: t('status.canceled') },
  [OrderState.COMPLETED]: { color: 'default', label: t('status.completed') },
  [OrderState.CREATED]: { color: 'primary', label: t('status.pending') },
  [OrderState.IN_PROGRESS]: { color: 'info', label: t('status.in_progress') },
  [OrderState.PENDING_REQUEST]: { color: 'default', label: t('status.pending') },
  [OrderState.REJECTED]: { color: 'error', label: t('status.rejected') },
};

export function getOrderStateLabel(state: OrderState) {
  const label = PROPS_BY_ORDER_STATE[state]?.label;
  return typeof label === 'string' ? label : snakeCaseToHuman(state);
}

interface OrderStateChipProps {
  hideDropdownArrow?: boolean;
  state: OrderState;
  sx?: SxProps<Theme>;
}

// State-only variant for callers that have an order state but not a full
// Order model (e.g. the rate-impact preview rows). OrderStatusChip below
// delegates here, so both render identically.
export function OrderStateChip({ hideDropdownArrow, state, sx }: OrderStateChipProps) {
  const { color } = PROPS_BY_ORDER_STATE[state] ?? { color: 'info' };
  const label = getOrderStateLabel(state);

  return (
    <Chip
      color={color}
      icon={hideDropdownArrow ? undefined : <ArrowDropDown />}
      label={label}
      size="small"
      variant="outlined"
      sx={{
        '&.MuiChip-root': {
          '&.MuiChip-outlined': { flexDirection: 'row-reverse', borderRadius: 1 },
          '.MuiChip-label': {
            fontSize: '12px',
            fontWeight: 700,
            ...(hideDropdownArrow ? { px: 0.5 } : { pl: 0.5, pr: 0 }),
          },
          '.MuiChip-icon': { mx: 0 },
        },
        ...sx,
      }}
    />
  );
}

interface OrderStatusChipProps {
  hideDropdownArrow?: boolean;
  order: Order;
  sx?: SxProps<Theme>;
}

export default function OrderStatusChip({
  hideDropdownArrow,
  order,
  sx,
}: OrderStatusChipProps) {
  return (
    <OrderStateChip hideDropdownArrow={hideDropdownArrow} state={order.state} sx={sx} />
  );
}
